import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, downloadLinksService } from '../services/api';
import type { DownloadLinkItem } from '../types';
import '../styles/AdminNews.css';

const emptyLink: DownloadLinkItem = {
    title: '',
    url: '',
    description: '',
    sortOrder: 0,
    isActive: true
};

const AdminDownloads: React.FC = () => {
    const navigate = useNavigate();
    const [list, setList] = useState<DownloadLinkItem[]>([]);
    const [selected, setSelected] = useState<DownloadLinkItem | null>(null);
    const [form, setForm] = useState<DownloadLinkItem>(emptyLink);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const checkAndLoad = async () => {
            if (!authService.isAuthenticated()) {
                navigate('/login');
                return;
            }
            if (!(await authService.checkIsAdmin())) {
                navigate('/');
                return;
            }
            await loadList();
        };
        checkAndLoad();
    }, [navigate]);

    const loadList = async () => {
        try {
            setLoading(true);
            const data = await downloadLinksService.getListAdmin();
            setList(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Não foi possível carregar os links.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (item: DownloadLinkItem | null) => {
        setSelected(item);
        if (item && item.id != null) {
            setForm({
                id: item.id,
                title: item.title,
                url: item.url,
                description: item.description ?? '',
                sortOrder: item.sortOrder ?? 0,
                isActive: item.isActive ?? true
            });
        } else {
            setForm(emptyLink);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const target = e.target;
        const name = target.name;
        const value = target.type === 'checkbox' ? (target as HTMLInputElement).checked : target.value;
        const num = name === 'sortOrder' ? parseInt(String(value), 10) : undefined;
        setForm((prev) => ({
            ...prev,
            [name]: num !== undefined ? (isNaN(num) ? 0 : num) : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSaving(true);
        try {
            if (form.id != null) {
                await downloadLinksService.update(form.id, form);
            } else {
                await downloadLinksService.create(form);
            }
            await loadList();
            handleSelect(null);
        } catch (err) {
            setError('Erro ao salvar. Tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (item: DownloadLinkItem) => {
        if (item.id == null) return;
        if (!window.confirm('Excluir este link?')) return;
        try {
            await downloadLinksService.delete(item.id);
            await loadList();
            if (selected?.id === item.id) handleSelect(null);
        } catch (err) {
            setError('Erro ao excluir.');
        }
    };

    return (
        <div className="admin-page admin-panel-content">
                <h1 className="admin-page-title">Downloads</h1>
                <p className="admin-page-subtitle">
                    Edite os links exibidos na página de Download. O texto do botão é o &quot;Título&quot;;
                    a URL é para onde o usuário será levado ao clicar. Ordem: menor número aparece primeiro.
                </p>

                {error && <div className="admin-error">{error}</div>}

                <div className="admin-grid">
                    <div className="admin-list">
                        <div className="admin-list-header">
                            <h2>Links</h2>
                            <button type="button" className="btn btn-secondary" onClick={() => handleSelect(null)}>
                                Novo link
                            </button>
                        </div>

                        {loading ? (
                            <div className="admin-loading">Carregando...</div>
                        ) : (
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Ordem</th>
                                        <th>Título</th>
                                        <th>Ativo</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.map((item) => (
                                        <tr
                                            key={item.id}
                                            className={selected?.id === item.id ? 'selected' : ''}
                                            onClick={() => handleSelect(item)}
                                        >
                                            <td>{item.sortOrder}</td>
                                            <td>{item.title}</td>
                                            <td>{item.isActive ? 'Sim' : 'Não'}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-small btn-danger"
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                                                >
                                                    Excluir
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {list.length === 0 && !loading && (
                                        <tr>
                                            <td colSpan={4}>Nenhum link. Adicione um para aparecer na página de Download.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="admin-form-wrapper">
                        <h2>{form.id ? 'Editar link' : 'Novo link'}</h2>
                        <form className="admin-form" onSubmit={handleSubmit}>
                            <div className="form-row">
                                <label htmlFor="title">Título (texto do botão)</label>
                                <input
                                    id="title"
                                    name="title"
                                    type="text"
                                    value={form.title}
                                    onChange={handleChange}
                                    required
                                    placeholder="Ex: Knight Icarus Online - New Client"
                                />
                            </div>
                            <div className="form-row">
                                <label htmlFor="url">URL</label>
                                <input
                                    id="url"
                                    name="url"
                                    type="url"
                                    value={form.url}
                                    onChange={handleChange}
                                    required
                                    placeholder="https://..."
                                />
                            </div>
                            <div className="form-row">
                                <label htmlFor="description">Descrição (opcional)</label>
                                <input
                                    id="description"
                                    name="description"
                                    type="text"
                                    value={form.description}
                                    onChange={handleChange}
                                    placeholder="Ex: Cliente principal"
                                />
                            </div>
                            <div className="form-row">
                                <label htmlFor="sortOrder">Ordem (0 = primeiro)</label>
                                <input
                                    id="sortOrder"
                                    name="sortOrder"
                                    type="number"
                                    min={0}
                                    value={form.sortOrder}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                <input
                                    id="isActive"
                                    name="isActive"
                                    type="checkbox"
                                    checked={form.isActive}
                                    onChange={handleChange}
                                />
                                <label htmlFor="isActive">Ativo (exibir na página de Download)</label>
                            </div>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? 'Salvando...' : (form.id ? 'Salvar' : 'Criar')}
                            </button>
                        </form>
                    </div>
                </div>
        </div>
    );
};

export default AdminDownloads;
